import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconGolf, IconSearch, IconEdit, IconX, IconDeviceFloppy, IconPlus, IconCheck, IconBan, IconUserSearch, IconTrash,
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm, IconTrophy
} from '@tabler/icons-react';
import api from '../api/api';

const WEATHER_OPTIONS = [
  { code: 'sunny',  label: '맑음',   Icon: IconSun,        color: '#f59e0b' },
  { code: 'cloudy', label: '흐림',   Icon: IconCloud,      color: '#6b7280' },
  { code: 'rain',   label: '비',     Icon: IconCloudRain,  color: '#3b82f6' },
  { code: 'snow',   label: '눈',     Icon: IconSnowflake,  color: '#60a5fa' },
  { code: 'wind',   label: '바람',   Icon: IconWind,       color: '#8b5cf6' },
  { code: 'storm',  label: '폭풍',   Icon: IconCloudStorm, color: '#ef4444' },
];

const WeatherIcon = ({ code, size = 16 }) => {
  const w = WEATHER_OPTIONS.find(o => o.code === code);
  if (!w) return null;
  return <w.Icon size={size} color={w.color} title={w.label} />;
};

// 홀 자동계산 뱃지
const HoleBadge = ({ sections }) => {
  if (!sections || sections.length === 0) return <span className="text-muted">-</span>;
  const h0 = sections[0]?.holesCount || 9;
  if (sections.length === 1) return <span className="badge bg-azure-lt" style={{ fontSize: 11 }}>{h0}홀</span>;
  const h1 = sections[1]?.holesCount || 9;
  if (sections[0]?.sectionId === sections[1]?.sectionId)
    return <span className="badge bg-purple-lt" style={{ fontSize: 11 }}>{h0}x2</span>;
  return <span className="badge bg-green-lt" style={{ fontSize: 11 }}>{h0 + h1}홀</span>;
};

const STATUS_MAP = {
  PENDING:   { label: '대기',   color: 'bg-yellow-lt' },
  CANCELLED: { label: '취소',   color: 'bg-red-lt'    },
  COMPLETED: { label: '완료',   color: 'bg-blue-lt'   },
};

const EMPTY_FORM = {
  golfCourseId: '',
  reservedUserId: '',
  reserverName: '',
  reserverTel: '',
  reservedDate: '',
  reservedTime: '',
  playerCount: 4,
  status: 'PENDING',
  memo: '',
  adminMemo: '',
  greenFee: '',
  weather: '',
  temperature: '',
  gameType: '일반',
  useYn: 'Y',
  sections: [],
};

export default function GolfReservationsPage({ me, showToast }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const PAGE_SIZE = 20;

  // 필터
  const [courseId, setCourseId] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterGameType, setFilterGameType] = useState('');
  const [filterUseYn, setFilterUseYn] = useState('');

  // 정렬
  const [sortField, setSortField] = useState('reservedDate');
  const [sortDir, setSortDir] = useState('desc');

  // 골프장 목록 (필터용)
  const [courses, setCourses] = useState([]);

  // 예약 상세/수정 모달
  const [detailModal, setDetailModal] = useState(null); // reservation 객체
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // 예약 추가 모달
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);

  // 마이그레이션
  const [migrating, setMigrating] = useState(false);

  // 스코어 모달
  const [scoreModal, setScoreModal] = useState(null); // reservation 객체
  const [scores, setScores] = useState([]);
  const [scoreSaving, setScoreSaving] = useState(false);

  // 섹션 목록 (골프장 선택 시 로드)
  const [courseSections, setCourseSections] = useState([]);

  const loadCourses = useCallback(async () => {
    try {
      const res = await api.get('/admin/golf/courses', { params: { size: 500 } });
      setCourses(res.data.content || []);
    } catch { /* ignore */ }
  }, []);

  const loadReservations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/golf/reservations', {
        params: {
          page, size: PAGE_SIZE,
          courseId: courseId || undefined,
          status: status || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          keyword: keyword || undefined,
          gameType: filterGameType || undefined,
          useYn: filterUseYn || undefined,
          sortField,
          sortDir,
        }
      });
      setReservations(res.data.content || []);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    } catch {
      showToast('예약 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, courseId, status, dateFrom, dateTo, keyword, filterGameType, filterUseYn, sortField, sortDir]);

  useEffect(() => { loadCourses(); }, [loadCourses]);
  useEffect(() => { loadReservations(); }, [loadReservations]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setKeyword(searchInput);
  };

  const loadSections = async (cId) => {
    if (!cId) { setCourseSections([]); return; }
    try {
      const res = await api.get(`/admin/golf/courses/${cId}/sections`);
      setCourseSections(res.data || []);
    } catch { setCourseSections([]); }
  };

  // 예약 추가 모달 열기
  const openAdd = () => {
    setAddForm({ ...EMPTY_FORM, reservedDate: new Date().toLocaleDateString('sv') });
    setCourseSections([]);
    setAddModal(true);
  };

  const handleAddCourseChange = async (cId) => {
    setAddForm(f => ({ ...f, golfCourseId: cId, sections: [] }));
    await loadSections(cId);
  };

  const handleAddSave = async () => {
    if (!addForm.golfCourseId) { showToast('골프장을 선택하세요.', 'error'); return; }
    if (!addForm.reservedDate || !addForm.reservedTime) { showToast('예약 날짜/시간을 입력하세요.', 'error'); return; }
    setAddSaving(true);
    try {
      const payload = buildPayload(addForm);
      await api.post('/admin/golf/reservations', payload);
      showToast('예약이 등록되었습니다.');
      setAddModal(false);
      loadReservations();
    } catch {
      showToast('등록에 실패했습니다.', 'error');
    } finally {
      setAddSaving(false);
    }
  };

  // 수정 모달 열기
  const openEdit = async (r) => {
    setDetailModal(null);
    await loadSections(r.golfCourseId);
    setEditForm({
      golfCourseId: r.golfCourseId,
      reservedUserId: r.reservedUserId || '',
      reserverName: r.reserverName || '',
      reserverTel: r.reserverTel || '',
      reservedDate: r.reservedDate || '',
      reservedTime: r.reservedTime ? r.reservedTime.slice(0, 5) : '',
      playerCount: r.playerCount || 4,
      status: r.status || 'PENDING',
      memo: r.memo || '',
      adminMemo: r.adminMemo || '',
      greenFee: r.greenFee != null ? String(r.greenFee) : '',
      weather: r.weather || '',
      temperature: r.temperature != null ? String(r.temperature) : '',
      gameType: r.gameType || '일반',
      useYn: r.useYn || 'Y',
      sections: r.sections ? r.sections.map(s => ({ sectionId: s.sectionId, playOrder: s.playOrder })) : [],
    });
    setDetailModal(r);
    setEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm.reservedDate || !editForm.reservedTime) { showToast('예약 날짜/시간을 입력하세요.', 'error'); return; }
    setSaving(true);
    try {
      const res = await api.put(`/admin/golf/reservations/${detailModal.id}`, buildPayload(editForm));
      setReservations(prev => prev.map(r => r.id === detailModal.id ? res.data : r));
      showToast('저장되었습니다.');
      setEditModal(false);
      setDetailModal(null);
    } catch {
      showToast('저장에 실패했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (id, newStatus) => {
    const labels = { CANCELLED: '취소', COMPLETED: '완료' };
    if (!window.confirm(`예약을 ${labels[newStatus]}처리 하시겠습니까?`)) return;
    try {
      const res = await api.patch(`/admin/golf/reservations/${id}/status`, null, { params: { status: newStatus } });
      setReservations(prev => prev.map(r => r.id === id ? res.data : r));
      if (detailModal?.id === id) setDetailModal(res.data);
      showToast(`${labels[newStatus]} 처리되었습니다.`);
    } catch {
      showToast('상태 변경에 실패했습니다.', 'error');
    }
  };

  const buildPayload = (form) => ({
    golfCourseId: form.golfCourseId ? Number(form.golfCourseId) : null,
    reservedUserId: form.reservedUserId ? Number(form.reservedUserId) : null,
    reserverName: form.reserverName || null,
    reserverTel: form.reserverTel || null,
    reservedDate: form.reservedDate || null,
    reservedTime: form.reservedTime ? (form.reservedTime.length === 5 ? form.reservedTime + ':00' : form.reservedTime) : null,
    playerCount: Number(form.playerCount) || 4,
    status: form.status || 'PENDING',
    memo: form.memo || null,
    adminMemo: form.adminMemo || null,
    greenFee: form.greenFee !== '' ? form.greenFee : null,
    weather: form.weather || null,
    temperature: form.temperature || null,
    gameType: form.gameType || null,
    useYn: form.useYn || 'Y',
    sections: form.sections,
  });

  const addSection = (form, setForm, sectionId) => {
    const sec = courseSections.find(s => s.id === Number(sectionId));
    setForm(f => ({
      ...f,
      sections: [...f.sections, { sectionId, playOrder: f.sections.length + 1, parData: sec?.parData || null }]
    }));
  };

  const removeOneSection = (form, setForm, sectionId) => {
    // 마지막 occurrence 하나만 제거
    const idx = [...form.sections].reverse().findIndex(s => s.sectionId === sectionId);
    if (idx === -1) return;
    const realIdx = form.sections.length - 1 - idx;
    const next = form.sections
      .filter((_, i) => i !== realIdx)
      .map((s, i) => ({ ...s, playOrder: i + 1 }));
    setForm(f => ({ ...f, sections: next }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('예약을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      await api.delete(`/admin/golf/reservations/${id}`);
      setReservations(prev => prev.filter(r => r.id !== id));
      setDetailModal(null);
      showToast('삭제되었습니다.');
    } catch {
      showToast('삭제에 실패했습니다.', 'error');
    }
  };

  const openScore = async (r, e) => {
    e.stopPropagation();
    setScoreModal(r);
    setScores([]);
    try {
      const res = await api.get(`/admin/golf/reservations/${r.id}/scores`);
      setScores(res.data || []);
    } catch {
      setScores([]);
    }
  };

  const handleScoreSave = async () => {
    setScoreSaving(true);
    try {
      await api.put(`/admin/golf/reservations/${scoreModal.id}/scores`, scores);
      showToast('스코어가 저장되었습니다.');
      setScoreModal(null);
    } catch {
      showToast('저장에 실패했습니다.', 'error');
    } finally {
      setScoreSaving(false);
    }
  };

  const [scoreMigrating, setScoreMigrating] = useState(false);
  const handleScoreMigrate = async () => {
    if (!window.confirm(
      'jp.golf_rounding_user 스코어 데이터를 마이그레이션합니다.\n' +
      '이미 마이그레이션된 데이터는 자동으로 건너뜁니다.\n\n계속하시겠습니까?'
    )) return;
    setScoreMigrating(true);
    try {
      const res = await api.post('/admin/golf/scores/migrate');
      const { migrated, skipped, errors } = res.data;
      showToast(`스코어 마이그레이션 완료 — 성공: ${migrated}건, 건너뜀: ${skipped}건, 오류: ${errors}건`);
    } catch {
      showToast('스코어 마이그레이션에 실패했습니다.', 'error');
    } finally {
      setScoreMigrating(false);
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm(
      'jp.golf_rounding 데이터를 golf_reservations 테이블로 마이그레이션합니다.\n' +
      '⚠️ 이미 마이그레이션된 경우 중복 데이터가 생성될 수 있습니다.\n\n계속하시겠습니까?'
    )) return;
    setMigrating(true);
    try {
      const res = await api.post('/admin/golf/reservations/migrate');
      const { migrated, skipped, errors } = res.data;
      showToast(`마이그레이션 완료 — 성공: ${migrated}건, 건너뜀: ${skipped}건, 오류: ${errors}건`);
      loadReservations();
    } catch {
      showToast('마이그레이션에 실패했습니다.', 'error');
    } finally {
      setMigrating(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  };

  const SortIndicator = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 3 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: '#206bc4' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const displayName = (r) => r.reservedUserName || r.reserverName || '-';
  const displayTel = (r) => r.reserverTel || '-';

  return (
    <div className="container-xl py-3">
      {/* 헤더 */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <IconGolf size={22} />
          <h4 className="mb-0">골프 예약 관리</h4>
          <span className="badge bg-blue-lt ms-1">{totalElements.toLocaleString()}건</span>
        </div>
        <div className="d-flex gap-2">
          {me?.role === 'MASTER' && <>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleScoreMigrate} disabled={scoreMigrating}
              title="jp.golf_rounding_user → golf_round_scores 스코어 마이그레이션">
              {scoreMigrating
                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 14, height: 14 }} />스코어 마이그 중...</>
                : '🏆 스코어 마이그레이션'}
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleMigrate} disabled={migrating}
              title="jp.golf_rounding → golf_reservations 마이그레이션">
              {migrating
                ? <><span className="spinner-border spinner-border-sm me-1" style={{ width: 14, height: 14 }} />마이그레이션 중...</>
                : '📥 마이그레이션'}
            </button>
          </>}
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            <IconPlus size={15} className="me-1" />예약 추가
          </button>
        </div>
      </div>

      {/* 필터 */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form className="row g-2 align-items-end flex-wrap" onSubmit={handleSearch}>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={courseId}
                onChange={e => { setCourseId(e.target.value); setPage(0); }}>
                <option value="">전체 골프장</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={status}
                onChange={e => { setStatus(e.target.value); setPage(0); }}>
                <option value="">전체 상태</option>
                {Object.entries(STATUS_MAP).map(([k, v]) =>
                  <option key={k} value={k}>{v.label}</option>
                )}
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterGameType}
                onChange={e => { setFilterGameType(e.target.value); setPage(0); }}>
                <option value="">경기유형 전체</option>
                <option value="일반">일반</option>
                <option value="메이저">메이저</option>
              </select>
            </div>
            <div className="col-auto">
              <select className="form-select form-select-sm" value={filterUseYn}
                onChange={e => { setFilterUseYn(e.target.value); setPage(0); }}>
                <option value="">유효 전체</option>
                <option value="Y">유효(Y)</option>
                <option value="N">무효(N)</option>
              </select>
            </div>
            <div className="col-auto">
              <input type="date" className="form-control form-control-sm" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(0); }} />
            </div>
            <div className="col-auto" style={{ lineHeight: '32px', color: '#aaa' }}>~</div>
            <div className="col-auto">
              <input type="date" className="form-control form-control-sm" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(0); }} />
            </div>
            <div className="col-auto">
              <div className="input-group input-group-sm">
                <input type="text" className="form-control form-control-sm" style={{ width: 150 }}
                  placeholder="예약자명, 연락처"
                  value={searchInput} onChange={e => setSearchInput(e.target.value)} />
                <button className="btn btn-sm btn-primary" type="submit"><IconSearch size={14} /></button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* 테이블 */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reservedDate')}>
                  예약일시<SortIndicator field="reservedDate" />
                </th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('golfCourse.name')}>
                  골프장<SortIndicator field="golfCourse.name" />
                </th>
                <th>코스</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('reserverName')}>
                  예약자<SortIndicator field="reserverName" />
                </th>
                <th>연락처</th>
                <th style={{ cursor: 'pointer', userSelect: 'none', width: 60 }} onClick={() => handleSort('playerCount')}>
                  인원<SortIndicator field="playerCount" />
                </th>
                <th style={{ width: 50 }}>홀</th>
                <th style={{ cursor: 'pointer', userSelect: 'none', width: 80 }} onClick={() => handleSort('greenFee')}>
                  그린피<SortIndicator field="greenFee" />
                </th>
                <th style={{ width: 70 }}>경기유형</th>
                <th style={{ width: 55 }}>유효</th>
                <th style={{ width: 80 }}>상태</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />불러오는 중...
                </td></tr>
              ) : reservations.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-4 text-muted">예약 내역이 없습니다.</td></tr>
              ) : reservations.map(r => {
                const st = STATUS_MAP[r.status] || { label: r.status, color: '' };
                return (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(r)}>
                    <td className="fw-medium" style={{ fontSize: 13 }}>
                      {r.reservedDate}<br />
                      <span className="d-inline-flex align-items-center gap-1 text-muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        <span>{r.reservedTime?.slice(0, 5)}</span>
                        {r.weather && <WeatherIcon code={r.weather} size={12} />}
                        {r.temperature && <span style={{ fontSize: 11 }}>{r.temperature}°</span>}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{r.golfCourseName}</td>
                    <td className="text-muted" style={{ fontSize: 12 }}>
                      {r.sections?.map(s => s.sectionName).join(' + ') || '-'}
                    </td>
                    <td style={{ fontSize: 13 }}>{displayName(r)}</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{displayTel(r)}</td>
                    <td className="text-muted" style={{ fontSize: 13 }}>{r.playerCount}명</td>
                    <td style={{ fontSize: 13 }}><HoleBadge sections={r.sections} /></td>
                    <td className="text-muted" style={{ fontSize: 13 }}>
                      {r.greenFee != null ? r.greenFee : '-'}
                    </td>
                    <td className="text-center" style={{ fontSize: 12 }}>
                      {r.gameType
                        ? <span className={`badge ${r.gameType === '메이저' ? 'bg-purple-lt' : 'bg-azure-lt'}`}>{r.gameType}</span>
                        : <span className="text-muted">-</span>}
                    </td>
                    <td className="text-center">
                      {r.useYn === 'N'
                        ? <span className="badge bg-red-lt">N</span>
                        : <span className="badge bg-green-lt">Y</span>}
                    </td>
                    <td><span className={`badge ${st.color}`}>{st.label}</span></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="d-flex gap-1">
                        {r.status === 'PENDING' && (
                          <button className="btn btn-sm btn-outline-primary"
                            onClick={() => handleStatus(r.id, 'COMPLETED')} title="완료">
                            <IconCheck size={13} />
                          </button>
                        )}
                        {r.status === 'PENDING' && (
                          <button className="btn btn-sm btn-outline-danger"
                            onClick={() => handleStatus(r.id, 'CANCELLED')} title="취소">
                            <IconBan size={13} />
                          </button>
                        )}
                        <button className="btn btn-sm btn-outline-secondary"
                          onClick={() => openEdit(r)} title="수정">
                          <IconEdit size={13} />
                        </button>
                        <button className="btn btn-sm btn-outline-warning"
                          onClick={(e) => openScore(r, e)} title="스코어 기록">
                          <IconTrophy size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="card-footer d-flex align-items-center justify-content-center">
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p - 1)}>이전</button>
              </li>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                const start = Math.max(0, Math.min(page - 4, totalPages - 10));
                const p = start + i;
                return (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p + 1}</button>
                  </li>
                );
              })}
              <li className={`page-item ${page >= totalPages - 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(p => p + 1)}>다음</button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* 상세 모달 */}
      {detailModal && !editModal && (
        <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 500 }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">예약 상세</h5>
                <button className="btn-close" onClick={() => setDetailModal(null)} />
              </div>
              <div className="modal-body">
                {(() => {
                  const r = detailModal;
                  const st = STATUS_MAP[r.status] || { label: r.status, color: '' };
                  return (
                    <table className="table table-sm table-borderless mb-0" style={{ fontSize: 14 }}>
                      <tbody>
                        <tr><th style={{ width: 100 }}>상태</th>
                          <td><span className={`badge ${st.color}`}>{st.label}</span></td></tr>
                        <tr><th>골프장</th><td>{r.golfCourseName}</td></tr>
                        <tr><th>코스</th><td>{r.sections?.map(s => `${s.sectionName}(${s.holesCount}홀)`).join(' + ') || '-'}</td></tr>
                        <tr><th>예약일</th><td>{r.reservedDate} {r.reservedTime?.slice(0, 5)}</td></tr>
                        <tr><th>인원</th><td>{r.playerCount}명 / <HoleBadge sections={r.sections} /></td></tr>
                        <tr><th>예약자</th><td>{displayName(r)}</td></tr>
                        <tr><th>연락처</th><td>{displayTel(r)}</td></tr>
                        {r.reservedUserEmail && <tr><th>이메일</th><td>{r.reservedUserEmail}</td></tr>}
                        <tr>
                          <th>그린피</th>
                          <td>{r.greenFee != null ? r.greenFee : '-'}</td>
                        </tr>
                        <tr>
                          <th>날씨</th>
                          <td>{r.weather
                            ? <span className="d-inline-flex align-items-center gap-1">
                                <WeatherIcon code={r.weather} size={16} />
                                <span>{WEATHER_OPTIONS.find(o => o.code === r.weather)?.label || r.weather}</span>
                              </span>
                            : '-'}</td>
                        </tr>
                        <tr><th>기온</th><td>{r.temperature || '-'}</td></tr>
                        <tr><th>경기유형</th><td>{r.gameType || '-'}</td></tr>
                        <tr>
                          <th>유효</th>
                          <td>{r.useYn === 'N'
                            ? <span className="badge bg-red-lt">N (무효)</span>
                            : <span className="badge bg-green-lt">Y (유효)</span>}</td>
                        </tr>
                        {r.memo && <tr><th>요청사항</th><td style={{ whiteSpace: 'pre-wrap' }}>{r.memo}</td></tr>}
                        {r.adminMemo && <tr><th>관리자 메모</th><td style={{ whiteSpace: 'pre-wrap' }}>{r.adminMemo}</td></tr>}
                        <tr><th>입력자</th><td>{r.createdUserName || '-'}</td></tr>
                        <tr><th>등록일</th><td>{r.createdAt?.slice(0, 16)}</td></tr>
                        {r.cancelledAt && <tr><th>취소일시</th><td>{r.cancelledAt?.slice(0, 16)}</td></tr>}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <div className="me-auto d-flex gap-1">
                  {detailModal.status === 'PENDING' && (
                    <button className="btn btn-primary btn-sm"
                      onClick={() => handleStatus(detailModal.id, 'COMPLETED')}>
                      <IconCheck size={15} className="me-1" />완료
                    </button>
                  )}
                  {detailModal.status === 'PENDING' && (
                    <button className="btn btn-danger btn-sm"
                      onClick={() => handleStatus(detailModal.id, 'CANCELLED')}>
                      <IconBan size={15} className="me-1" />취소
                    </button>
                  )}
                </div>
                <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(detailModal.id)}>
                  <IconTrash size={15} className="me-1" />삭제
                </button>
                <button className="btn btn-outline-warning btn-sm" onClick={(e) => openScore(detailModal, e)}>
                  <IconTrophy size={15} className="me-1" />스코어
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => openEdit(detailModal)}>
                  <IconEdit size={15} className="me-1" />수정
                </button>
                <button className="btn btn-sm" onClick={() => setDetailModal(null)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 스코어 모달 */}
      {scoreModal && (
        <ScoreModal
          reservation={scoreModal}
          scores={scores}
          saving={scoreSaving}
          onClose={() => setScoreModal(null)}
          onSave={handleScoreSave}
        />
      )}

      {/* 수정 모달 */}
      {editModal && detailModal && (
        <ReservationFormModal
          title="예약 수정"
          form={editForm}
          setForm={setEditForm}
          courses={courses}
          courseSections={courseSections}
          onCourseChange={(cId) => loadSections(cId)}
          onSave={handleEditSave}
          onClose={() => { setEditModal(false); setDetailModal(null); }}
          saving={saving}
          addSection={(sId) => addSection(editForm, setEditForm, sId)}
          removeOneSection={(sId) => removeOneSection(editForm, setEditForm, sId)}
        />
      )}

      {/* 추가 모달 */}
      {addModal && (
        <ReservationFormModal
          title="예약 추가"
          form={addForm}
          setForm={setAddForm}
          courses={courses.filter(c => c.useYn !== 'N')}
          courseSections={courseSections}
          onCourseChange={handleAddCourseChange}
          onSave={handleAddSave}
          onClose={() => setAddModal(false)}
          saving={addSaving}
          addSection={(sId) => addSection(addForm, setAddForm, sId)}
          removeOneSection={(sId) => removeOneSection(addForm, setAddForm, sId)}
        />
      )}
    </div>
  );
}

function ReservationFormModal({ title, form, setForm, courses, courseSections, onCourseChange, onSave, onClose, saving, addSection, removeOneSection }) {
  // ── 1. 골프장 자동완성 ──────────────────────────────────────
  const [courseInput, setCourseInput] = useState(() => {
    if (!form.golfCourseId) return '';
    const c = courses.find(c => String(c.id) === String(form.golfCourseId));
    return c ? c.name : '';
  });
  const [showCourseList, setShowCourseList] = useState(false);
  const courseRef = useRef(null);

  const filteredCourses = courseInput
    ? courses.filter(c => c.name.toLowerCase().includes(courseInput.toLowerCase())).slice(0, 30)
    : courses.slice(0, 30);

  const selectCourse = (c) => {
    setCourseInput(c.name);
    setShowCourseList(false);
    onCourseChange(String(c.id));
  };

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handler = (e) => {
      if (courseRef.current && !courseRef.current.contains(e.target)) setShowCourseList(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── 2. 회원 비동기 자동완성 ─────────────────────────────────
  const [memberInput, setMemberInput] = useState(() => {
    // 수정 시 기존 선택된 회원 표시
    return form.reservedUserId ? `#${form.reservedUserId}` : '';
  });
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const memberRef = useRef(null);
  const memberTimerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (memberRef.current && !memberRef.current.contains(e.target)) setShowMemberList(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMemberInput = (val) => {
    setMemberInput(val);
    setShowMemberList(true);
    // 선택 해제
    if (!val) setForm(f => ({ ...f, reservedUserId: '' }));
    // 디바운스
    clearTimeout(memberTimerRef.current);
    if (val.trim().length < 1) { setMemberResults([]); return; }
    memberTimerRef.current = setTimeout(async () => {
      setMemberSearching(true);
      try {
        const res = await api.get('/admin/users', { params: { keyword: val, size: 10 } });
        setMemberResults(res.data.users || []);
      } catch {
        setMemberResults([]);
      } finally {
        setMemberSearching(false);
      }
    }, 300);
  };

  const selectMember = (u) => {
    setMemberInput(`#${u.id}  ${u.name}${u.nickname && u.nickname !== u.name ? '  (' + u.nickname + ')' : ''}`);
    setShowMemberList(false);
    setMemberResults([]);
    setForm(f => ({ ...f, reservedUserId: u.id, reserverName: f.reserverName || u.name || '' }));
  };

  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 540 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="row g-2">

              {/* ── 1. 예약날짜 | 티타임 ── */}
              <div className="col-6">
                <label className="form-label mb-1">예약 날짜 <span className="text-danger">*</span></label>
                <input type="date" className="form-control form-control-sm" value={form.reservedDate}
                  onChange={e => setForm(f => ({ ...f, reservedDate: e.target.value }))} />
              </div>
              <div className="col-6">
                <label className="form-label mb-1">티타임 <span className="text-danger">*</span></label>
                <input type="text" className="form-control form-control-sm" placeholder="0930 또는 09:30" maxLength={5}
                  value={form.reservedTime}
                  onChange={e => setForm(f => ({ ...f, reservedTime: e.target.value.replace(/[^0-9:]/g, '') }))}
                  onBlur={e => {
                    const d = e.target.value.replace(/\D/g, '');
                    if (d.length === 3) setForm(f => ({ ...f, reservedTime: `0${d[0]}:${d.slice(1)}` }));
                    else if (d.length === 4) setForm(f => ({ ...f, reservedTime: `${d.slice(0,2)}:${d.slice(2)}` }));
                  }}
                />
              </div>

              {/* ── 2. 골프장 + 코스 (col-6 | col-3 | col-3) ── */}
              <div className="col-6">
                    <label className="form-label mb-1">골프장 <span className="text-danger">*</span></label>
                    <div ref={courseRef} style={{ position: 'relative' }}>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="골프장명 입력 또는 검색"
                        value={courseInput} autoComplete="off"
                        onChange={e => {
                          setCourseInput(e.target.value);
                          setShowCourseList(true);
                          if (!e.target.value) { setForm(f => ({ ...f, golfCourseId: '' })); onCourseChange(''); }
                        }}
                        onFocus={() => setShowCourseList(true)}
                      />
                      {showCourseList && filteredCourses.length > 0 && (
                        <ul style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                          maxHeight: 200, overflowY: 'auto', margin: 0, padding: 0,
                          listStyle: 'none', background: 'var(--tblr-card-bg, #fff)',
                          border: '1px solid var(--tblr-border-color)', borderRadius: '0 0 4px 4px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                        }}>
                          {filteredCourses.map(c => (
                            <li key={c.id}
                              style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                                background: String(form.golfCourseId) === String(c.id) ? 'var(--tblr-primary-lt)' : undefined }}
                              onMouseDown={() => selectCourse(c)}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--tblr-active-bg, #f1f5f9)'}
                              onMouseLeave={e => e.currentTarget.style.background = String(form.golfCourseId) === String(c.id) ? 'var(--tblr-primary-lt)' : ''}>
                              {c.name}
                              {c.address && <span className="text-muted ms-2" style={{ fontSize: 11 }}>{c.address}</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
              </div>
              {courseSections.length > 0 && (
                <>
                  <div className="col-3">
                    <label className="form-label mb-1">전반</label>
                    <select className="form-select form-select-sm"
                      value={form.sections[0]?.sectionId || ''}
                      onChange={e => {
                        const secId = e.target.value ? Number(e.target.value) : null;
                        const sec = courseSections.find(s => s.id === secId);
                        setForm(f => {
                          const second = f.sections[1] || null;
                          if (secId) {
                            const first = { sectionId: secId, playOrder: 1, parData: sec?.parData || null };
                            return { ...f, sections: second ? [first, second] : [first] };
                          }
                          return { ...f, sections: second ? [{ ...second, playOrder: 1 }] : [] };
                        });
                      }}>
                      <option value="">선택</option>
                      {courseSections.filter(s => s.useYn === 'Y').map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.holesCount || 9}홀)</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-3">
                    <label className="form-label mb-1">후반</label>
                    <select className="form-select form-select-sm"
                      value={form.sections[1]?.sectionId || ''}
                      onChange={e => {
                        const secId = e.target.value ? Number(e.target.value) : null;
                        const sec = courseSections.find(s => s.id === secId);
                        setForm(f => {
                          const first = f.sections[0] || null;
                          if (secId) {
                            const second = { sectionId: secId, playOrder: 2, parData: sec?.parData || null };
                            return { ...f, sections: first ? [first, second] : [{ sectionId: secId, playOrder: 1, parData: sec?.parData || null }] };
                          }
                          return { ...f, sections: first ? [first] : [] };
                        });
                      }}>
                      <option value="">선택</option>
                      {courseSections.filter(s => s.useYn === 'Y').map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.holesCount || 9}홀)</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* ── 4. 인원 | 상태 | 그린피 ── */}
              <div className="col-4">
                <label className="form-label mb-1">인원</label>
                <input type="number" className="form-control form-control-sm" min={1} max={20} value={form.playerCount}
                  onChange={e => setForm(f => ({ ...f, playerCount: e.target.value }))} />
              </div>
              <div className="col-4">
                <label className="form-label mb-1">상태</label>
                <select className="form-select form-select-sm" value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.entries(STATUS_MAP).map(([k, v]) =>
                    <option key={k} value={k}>{v.label}</option>
                  )}
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">그린피</label>
                <input type="text" className="form-control form-control-sm" placeholder="만원 또는 쿠폰 등" value={form.greenFee}
                  onChange={e => setForm(f => ({ ...f, greenFee: e.target.value }))} />
              </div>

              {/* ── 5. 회원(예약자) (6) | 예약자명 (3) | 연락처 (3) ── */}
              <div className="col-6" style={{ minWidth: 0 }}>
                <label className="form-label mb-1">
                  회원
                  {form.reservedUserId && (
                    <span className="ms-1" style={{ fontSize: 10, color: '#6c757d', border: '1px solid #dee2e6', borderRadius: 3, padding: '1px 4px' }}>
                      #{form.reservedUserId}
                    </span>
                  )}
                </label>
                {/* input-group 대신 position:relative 오버레이 방식 — 레이아웃 변동 없음 */}
                <div ref={memberRef} style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                    pointerEvents: 'none', zIndex: 2, display: 'flex', color: '#6c757d'
                  }}>
                    {memberSearching
                      ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} />
                      : <IconUserSearch size={13} />}
                  </span>
                  <input type="text" className="form-control form-control-sm"
                    style={{ paddingLeft: 26, paddingRight: 22 }}
                    placeholder="no, 이름, id" autoComplete="off"
                    value={memberInput}
                    onChange={e => handleMemberInput(e.target.value)}
                    onFocus={() => memberResults.length > 0 && setShowMemberList(true)}
                  />
                  {/* visibility 로 공간 유지 → 나타날 때 레이아웃 점프 없음 */}
                  <button type="button" tabIndex={-1}
                    style={{
                      position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer', zIndex: 2,
                      display: 'flex', alignItems: 'center',
                      visibility: memberInput ? 'visible' : 'hidden',
                    }}
                    onClick={() => { setMemberInput(''); setMemberResults([]); setShowMemberList(false); setForm(f => ({ ...f, reservedUserId: '' })); }}>
                    <IconX size={12} color="#aaa" />
                  </button>
                  {showMemberList && memberResults.length > 0 && (
                    <ul style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                      maxHeight: 180, overflowY: 'auto', margin: 0, padding: 0,
                      listStyle: 'none', background: 'var(--tblr-card-bg, #fff)',
                      border: '1px solid var(--tblr-border-color)', borderRadius: '0 0 4px 4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                    }}>
                      {memberResults.map(u => (
                        <li key={u.id}
                          style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                            background: String(form.reservedUserId) === String(u.id) ? 'var(--tblr-primary-lt)' : undefined }}
                          onMouseDown={() => selectMember(u)}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--tblr-active-bg, #f1f5f9)'}
                          onMouseLeave={e => e.currentTarget.style.background = String(form.reservedUserId) === String(u.id) ? 'var(--tblr-primary-lt)' : ''}>
                          <span style={{ fontSize: 10, color: '#999', border: '1px solid #dee2e6', borderRadius: 3, padding: '1px 3px' }}>#{u.id}</span>
                          <span className="fw-medium ms-1">{u.name}</span>
                          {u.nickname && u.nickname !== u.name && (
                            <span className="text-muted ms-1" style={{ fontSize: 11 }}>({u.nickname})</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {showMemberList && !memberSearching && memberInput && memberResults.length === 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                      padding: '7px 10px', fontSize: 12, color: '#999',
                      background: 'var(--tblr-card-bg, #fff)',
                      border: '1px solid var(--tblr-border-color)', borderRadius: '0 0 4px 4px'
                    }}>
                      검색 결과 없음
                    </div>
                  )}
                </div>
              </div>
              <div className="col-3">
                <label className="form-label mb-1">예약자명</label>
                <input type="text" className="form-control form-control-sm" placeholder="비회원"
                  value={form.reserverName}
                  onChange={e => setForm(f => ({ ...f, reserverName: e.target.value }))} />
              </div>
              <div className="col-3">
                <label className="form-label mb-1">연락처</label>
                <input type="text" className="form-control form-control-sm" placeholder="010-0000-0000"
                  value={form.reserverTel}
                  onChange={e => setForm(f => ({ ...f, reserverTel: e.target.value }))} />
              </div>

              {/* ── 6. 경기유형 (4) | 유효 (4) | 기온 (4) ── */}
              <div className="col-4">
                <label className="form-label mb-1">경기유형</label>
                <select className="form-select form-select-sm" value={form.gameType}
                  onChange={e => setForm(f => ({ ...f, gameType: e.target.value }))}>
                  <option value="일반">일반</option>
                  <option value="메이저">메이저</option>
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">유효</label>
                <select className="form-select form-select-sm" value={form.useYn}
                  onChange={e => setForm(f => ({ ...f, useYn: e.target.value }))}>
                  <option value="Y">Y (유효)</option>
                  <option value="N">N (무효)</option>
                </select>
              </div>
              <div className="col-4">
                <label className="form-label mb-1">기온</label>
                <input type="text" className="form-control form-control-sm" placeholder="예: 13  또는  4~9" value={form.temperature}
                  onChange={e => setForm(f => ({ ...f, temperature: e.target.value }))} />
              </div>

              {/* ── 7. 날씨 (12) ── */}
              <div className="col-12">
                <label className="form-label mb-1">날씨</label>
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  {WEATHER_OPTIONS.map(w => (
                    <button key={w.code} type="button" title={w.label}
                      className={`btn btn-sm ${form.weather === w.code ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ padding: '3px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      onClick={() => setForm(f => ({ ...f, weather: f.weather === w.code ? '' : w.code }))}>
                      <w.Icon size={13} color={form.weather === w.code ? '#fff' : w.color} />
                      <span style={{ fontSize: 12 }}>{w.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── 8. 요청사항 ── */}
              <div className="col-12">
                <label className="form-label mb-1">요청사항</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.memo}
                  onChange={e => setForm(f => ({ ...f, memo: e.target.value }))} />
              </div>

              {/* ── 9. 관리자 메모 ── */}
              <div className="col-12">
                <label className="form-label mb-1">관리자 메모</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.adminMemo}
                  onChange={e => setForm(f => ({ ...f, adminMemo: e.target.value }))} />
              </div>

            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-sm" onClick={onClose}>
              <IconX size={14} className="me-1" />취소
            </button>
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
              <IconDeviceFloppy size={14} className="me-1" />
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ScoreModal ────────────────────────────────────────────────────────────────
function ScoreModal({ reservation, scores, saving, onClose, onSave }) {
  // 홀별 JSON 파싱 헬퍼
  const parseHoles = (jsonStr, key) => {
    if (!jsonStr) return {};
    try { return JSON.parse(jsonStr)[key] || {}; } catch { return {}; }
  };

  // 오버파 값 색상
  const overColor = (v) => {
    const n = Number(v);
    if (!v && v !== 0) return '#aaa';
    if (n < 0) return '#2fb344';   // birdie 이하 — 초록
    if (n === 0) return '#1c7ed6';  // par — 파랑
    if (n === 1) return '#f76707';  // bogey — 주황
    return '#e03131';               // 더블보기 이상 — 빨강
  };

  const fmtOver = (v) => {
    if (v === '' || v == null) return '-';
    const n = Number(v);
    return n > 0 ? `+${n}` : String(n);
  };

  const hasHoles = scores.some(s => s.holeScoresJson);
  const sections = ['1', '2'];

  return (
    <div className="modal modal-blur show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 860 }}>
        <div className="modal-content">

          {/* 헤더 */}
          <div className="modal-header py-2">
            <div>
              <h5 className="modal-title d-flex align-items-center gap-2 mb-0">
                <IconTrophy size={17} style={{ color: '#f59e0b' }} />
                라운딩 스코어
              </h5>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                {reservation.golfCourseName} · {reservation.reservedDate} {reservation.reservedTime?.slice(0, 5)}
                {reservation.gameType && <span className="ms-2 badge bg-azure-lt">{reservation.gameType}</span>}
              </div>
            </div>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body p-0">
            {scores.length === 0 ? (
              <div className="text-center py-5 text-muted" style={{ fontSize: 14 }}>
                스코어 데이터가 없습니다.
              </div>
            ) : (
              <>
                {/* ── 요약 테이블 ─────────────────────────── */}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                    <thead className="table-light text-center">
                      <tr>
                        <th style={{ width: 30 }}>#</th>
                        <th style={{ width: 120, textAlign: 'left' }}>이름</th>
                        <th style={{ width: 60 }}>총타수</th>
                        <th style={{ width: 60 }}>오버파</th>
                        <th style={{ width: 55 }}>퍼팅</th>
                        <th style={{ width: 55 }}>머니</th>
                        <th style={{ width: 55 }}>드라이버</th>
                        <th style={{ width: 45 }}>참가</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scores.map(s => (
                        <tr key={s.playerSlot}
                          style={{ background: s.joinYn === 'N' ? '#f8f9fa' : undefined, opacity: s.joinYn === 'N' ? 0.6 : 1 }}>
                          <td className="text-center text-muted">{s.playerSlot}</td>
                          <td className="fw-medium">{s.playerName || <span className="text-muted">-</span>}</td>
                          <td className="text-center fw-bold">{s.totalScore ?? '-'}</td>
                          <td className="text-center fw-bold" style={{ color: overColor(s.overPar) }}>
                            {fmtOver(s.overPar)}
                          </td>
                          <td className="text-center text-muted">{s.totalPutting ?? '-'}</td>
                          <td className="text-center text-muted">{s.money != null && s.money !== 0 ? s.money.toLocaleString() : '-'}</td>
                          <td className="text-center text-muted">{s.car != null && s.car !== 0 ? s.car.toLocaleString() : '-'}</td>
                          <td className="text-center">
                            {s.joinYn === 'N'
                              ? <span className="badge bg-red-lt">N</span>
                              : <span className="badge bg-green-lt">Y</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── 스코어카드 (홀별) ─────────────────── */}
                {hasHoles && sections.map(sec => {
                  const hasSection = scores.some(s => {
                    const h = parseHoles(s.holeScoresJson, `no${sec}`);
                    return Object.values(h).some(v => v !== '' && v != null);
                  });
                  if (!hasSection) return null;
                  const holes = ['1','2','3','4','5','6','7','8','9'];
                  return (
                    <div key={sec}>
                      <div className="px-3 py-1 bg-light border-top border-bottom"
                        style={{ fontSize: 11, fontWeight: 600, color: '#666', letterSpacing: 0.5 }}>
                        ▸ 코스 {sec}  (홀당 오버파)
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0 text-center"
                          style={{ fontSize: 12, minWidth: 580 }}>
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: 100, textAlign: 'left' }}>이름</th>
                              {holes.map(h => <th key={h} style={{ width: 46 }}>H{h}</th>)}
                              <th style={{ width: 50 }}>합계</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scores.filter(s => s.joinYn !== 'N').map(s => {
                              const hd = parseHoles(s.holeScoresJson, `no${sec}`);
                              const pt = parseHoles(s.holeScoresJson, `putting${sec}`);
                              const vals = holes.map(h => hd[h]);
                              const sum = vals.reduce((acc, v) => acc + (v !== '' && v != null ? Number(v) : 0), 0);
                              return (
                                <tr key={s.playerSlot}>
                                  <td style={{ textAlign: 'left', fontWeight: 500 }}>{s.playerName || `P${s.playerSlot}`}</td>
                                  {vals.map((v, i) => {
                                    const putting = pt[holes[i]];
                                    return (
                                      <td key={i} style={{ color: overColor(v), fontWeight: 600, position: 'relative' }}>
                                        {v !== '' && v != null ? fmtOver(v) : <span style={{ color: '#ccc' }}>-</span>}
                                        {putting !== '' && putting != null && (
                                          <div style={{ fontSize: 9, color: '#888', lineHeight: 1 }}>{putting}P</div>
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td style={{ fontWeight: 700, color: overColor(sum) }}>{fmtOver(sum)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn btn-sm" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  );
}
